import React, { useEffect, useState, useRef } from "react";

// --- Helper: Format Date ---
const toDateKey = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
};

async function fetchAssetId(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const text = await res.text();
    const val = parseInt(text.trim(), 10);
    return isNaN(val) ? null : val;
  } catch (e) {
    return null;
  }
}

const ThreeDCesiumLayer = ({ viewer, latestSurvey, apiBase }) => {
  const [layers, setLayers] = useState([]);
  const isMountedRef = useRef(true);
  // Track the currently loaded asset ID to prevent reloading the same one
  const currentAssetIdRef = useRef(null);

  useEffect(() => {
    if (!viewer || !latestSurvey) return;
    isMountedRef.current = true;

    const discoverLayers = async () => {
      try {
        // A. Prepare API params
        const loc = String(latestSurvey.location_name).toLowerCase().replace(/[^a-z0-9-]+/g, '-');
        const sv = toDateKey(latestSurvey.survey_time);
        const params = new URLSearchParams({
          location_name: latestSurvey.location_name,
          survey_time: sv,
        });

        // B. Call Discovery API
        const discoveryUrl = `${apiBase}/layers3d/tiles-discovery?${params.toString()}`;
        console.log(`[3D Discovery] Fetching list: ${discoveryUrl}`);

        const r = await fetch(discoveryUrl);
        if (!r.ok) return;

        const json = await r.json();
        // The API returns a list of found candidates
        const candidates = Array.isArray(json.found) ? json.found : [];

        if (candidates.length === 0) {
          console.log("[3D Discovery] No 3D layers found.");
          return;
        }

        // C. Process ALL candidates in parallel
        const processedResults = await Promise.all(candidates.map(async (cand) => {
          const layerSlug = cand.layer_slug || cand.layer_name;
          
          // Construct URL: /3draster/{loc}/{date}/{layer_name}/tiles/token.txt
          const tokenUrl = `${apiBase}/3draster/${loc}/${sv}/${layerSlug}/tiles/token.txt`;
          
          const assetId = await fetchAssetId(tokenUrl);
          
          if (assetId !== null) {
            return {
              id: `auto-raster3d-${layerSlug}`, // Unique ID
              name: layerSlug,                  // Name for Sidebar
              assetId: assetId,
              visible: true,                    // Default to Visible
              opacity: 1.0
            };
          }
          return null;
        }));

        // Filter out failed tokens
        const validLayers = processedResults.filter(l => l !== null);

        if (!isMountedRef.current) return;

        if (validLayers.length > 0) {
          console.log(`[3D Layer] Loaded ${validLayers.length} layers:`, validLayers.map(l => l.name));
          setLayers(validLayers);

          // D. Dispatch to Sidebar
          // We send the LIST of names so the sidebar creates a toggle for EACH one.
          window.dispatchEvent(new CustomEvent("raster3d-layers-discovered", {
            detail: {
              location: latestSurvey.location_name,
              survey_time: sv,
              layer_names: validLayers.map(l => l.name), // Array of names
              candidates: validLayers // Full data for sidebar if needed
            }
          }));
        } else {
          setLayers([]);
        }

      } catch (e) {
        console.error("[3D Discovery] Error:", e);
      }
    };

    discoverLayers();

    return () => { isMountedRef.current = false; };
  }, [latestSurvey, apiBase, viewer]);

  useEffect(() => {
    const handleToggle = (ev) => {
      const { id, visible } = ev.detail || {};
      setLayers(prev => prev.map(l => {
        // Match by Name or ID
        if (l.name === id || l.id === id) {
          console.log(`[3D Layer] Toggled ${l.name}: ${visible}`);
          return { ...l, visible: !!visible };
        }
        return l;
      }));
    };

    const handleOpacity = (ev) => {
      const { id, opacity } = ev.detail || {};
      setLayers(prev => prev.map(l => {
        if (l.name === id || l.id === id) {
          return { ...l, opacity: Number(opacity) };
        }
        return l;
      }));
    };

    window.addEventListener("map-toggle-layer", handleToggle);
    window.addEventListener("map-set-opacity", handleOpacity);

    return () => {
      window.removeEventListener("map-toggle-layer", handleToggle);
      window.removeEventListener("map-set-opacity", handleOpacity);
    };
  }, []);

  // ----------------------------------------------------
  // 3. RENDER LOGIC (Apply to Cesium Viewer)
  // ----------------------------------------------------
  useEffect(() => {
    if (!viewer) return;

    const Cesium = window.Cesium;

    const updateViewer = async () => {
      const activeLayer = [...layers].reverse().find(l => l.visible);

      // B. If no layer is visible, reset to Flat Earth
      if (!activeLayer) {
        if (viewer.terrainProvider instanceof Cesium.CesiumTerrainProvider) {
          console.log("[3D Layer] No active layers. Resetting to Ellipsoid.");
          viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider();
          viewer.scene.globe.depthTestAgainstTerrain = false;
          viewer.scene.globe.enableLighting = false;
          currentAssetIdRef.current = null;
        }
        return;
      }

      // C. If the required asset is already loaded, do nothing
      if (currentAssetIdRef.current === activeLayer.assetId) {
        return;
      }

      // D. Load the new Asset
      try {
        console.log(`[3D Layer] Switching Terrain to: ${activeLayer.name} (ID: ${activeLayer.assetId})`);
        
        const provider = await Cesium.CesiumTerrainProvider.fromIonAssetId(activeLayer.assetId, {
          requestVertexNormals: true
        });

        if (isMountedRef.current) {
          viewer.terrainProvider = provider;
          viewer.scene.globe.depthTestAgainstTerrain = true;
          viewer.scene.globe.enableLighting = true;
          viewer.scene.globe.baseColor = Cesium.Color.LIGHTGRAY;
          
          currentAssetIdRef.current = activeLayer.assetId;
        }
      } catch (e) {
        console.error(`[3D Layer] Failed to load ${activeLayer.name}:`, e);
      }
    };

    updateViewer();

  }, [viewer, layers]); 

  useEffect(() => {
    return () => {
      if (viewer && !viewer.isDestroyed()) {
        viewer.terrainProvider = new window.Cesium.EllipsoidTerrainProvider();
        viewer.scene.globe.depthTestAgainstTerrain = false;
      }
    };
  }, [viewer]);

  return null;
};

export default ThreeDCesiumLayer;